import React, { useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import ContentEditable, { ContentEditableEvent } from 'react-contenteditable';
import { cn } from '@/lib/utils';

export interface Mention {
  type: 'file' | 'playbook';
  id: string;
  name: string;
}

export interface MentionInputHandle {
  focus: () => void;
  clear: () => void;
  /** Replace the whole input with plain text (used to pre-fill a suggestion) */
  setText: (value: string) => void;
  /** Insert a mention chip, replacing the trailing @ in the text */
  insertMention: (mention: Mention) => void;
}

interface MentionInputProps {
  mentions: Mention[];
  onMentionsChange: (mentions: Mention[]) => void;
  onTextChange: (text: string) => void;
  onAtTrigger: () => void;
  onAtRemoved: () => void;
  onSubmit: () => void;
  /** When true, Enter key won't trigger onSubmit (e.g. when a menu is open) */
  suppressSubmit?: boolean;
  /** Called when '/' is typed at position 0, with the filter text after '/' */
  onSlashTrigger?: (filter: string) => void;
  /** Called when the leading '/' is removed */
  onSlashRemoved?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

// Build HTML string from plaintext, replacing @mention names with chip spans
function buildHtml(text: string, mentions: Mention[]): string {
  if (mentions.length === 0) return escapeHtml(text).replace(/\n/g, '<br>');

  let html = '';
  let remaining = text;

  // Sort mentions by their position in the text (tokens are [[type:uuid]])
  const sortedMentions = mentions
    .map(m => ({ ...m, token: `[[${m.type}:${m.id}]]`, index: remaining.indexOf(`[[${m.type}:${m.id}]]`) }))
    .filter(m => m.index !== -1)
    .sort((a, b) => a.index - b.index);

  let offset = 0;
  for (const mention of sortedMentions) {
    const idx = remaining.indexOf(mention.token, offset);
    if (idx === -1) continue;

    // Text before this mention
    html += escapeHtml(remaining.slice(offset, idx)).replace(/\n/g, '<br>');
    // Chip span
    html += buildChipHtml(mention);
    offset = idx + mention.token.length;
  }

  // Remaining text after last mention
  html += escapeHtml(remaining.slice(offset)).replace(/\n/g, '<br>');

  return html;
}

function buildChipHtml(mention: Mention): string {
  const icon = mention.type === 'playbook'
    ? `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M12 7v14"/><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"/></svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>`;

  return `<span contenteditable="false" data-mention-id="${escapeAttr(mention.id)}" data-mention-type="${mention.type}" data-mention-name="${escapeAttr(mention.name)}" class="mention-chip">${icon}<span>${escapeHtml(mention.name)}</span></span>`;
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(str: string): string {
  return str.replace(/"/g, '&quot;').replace(/&/g, '&amp;');
}

// Extract plain text from the contenteditable HTML, preserving @mention references
function extractText(el: HTMLElement): string {
  let text = '';
  el.childNodes.forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent || '';
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      if (element.dataset.mentionId) {
        text += `[[${element.dataset.mentionType || 'file'}:${element.dataset.mentionId}]]`;
      } else if (element.tagName === 'BR') {
        text += '\n';
      } else {
        text += extractText(element);
      }
    }
  });
  return text;
}

// Extract mentions from the DOM
function extractMentions(el: HTMLElement): Mention[] {
  const mentions: Mention[] = [];
  el.querySelectorAll('[data-mention-id]').forEach(chip => {
    const htmlEl = chip as HTMLElement;
    mentions.push({
      id: htmlEl.dataset.mentionId!,
      type: htmlEl.dataset.mentionType as 'file' | 'playbook',
      name: htmlEl.dataset.mentionName || '',
    });
  });
  return mentions;
}

export const MentionInput = forwardRef<MentionInputHandle, MentionInputProps>(({
  mentions,
  onMentionsChange,
  onTextChange,
  onAtTrigger,
  onAtRemoved,
  onSubmit,
  suppressSubmit,
  onSlashTrigger,
  onSlashRemoved,
  placeholder,
  disabled,
  className,
}, ref) => {
  const editableRef = useRef<HTMLDivElement>(null);
  const htmlRef = useRef<string>('');
  const isComposing = useRef(false);
  const prevMentionIds = useRef<string>('');
  const suppressSubmitRef = useRef(suppressSubmit);
  suppressSubmitRef.current = suppressSubmit;

  useImperativeHandle(ref, () => ({
    focus: () => editableRef.current?.focus(),
    clear: () => {
      htmlRef.current = '';
      prevMentionIds.current = '';
      if (editableRef.current) {
        editableRef.current.innerHTML = '';
      }
    },
    setText: (value: string) => {
      const el = editableRef.current;
      if (!el) return;
      const html = escapeHtml(value).replace(/\n/g, '<br>');
      el.innerHTML = html;
      htmlRef.current = html;
      prevMentionIds.current = '';
      onTextChange(value);
      // Focus and place the cursor at the end.
      el.focus();
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    },
    insertMention: (mention: Mention) => {
      const el = editableRef.current;
      if (!el) return;

      // Walk text nodes to find trailing @
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      let targetNode: Text | null = null;
      let atIndex = -1;

      while (walker.nextNode()) {
        const node = walker.currentNode as Text;
        const text = node.textContent || '';
        const idx = text.lastIndexOf('@');
        if (idx !== -1) {
          targetNode = node;
          atIndex = idx;
        }
      }

      if (targetNode && atIndex !== -1) {
        const text = targetNode.textContent || '';
        const before = text.slice(0, atIndex);
        const after = text.slice(atIndex + 1); // text after @
        targetNode.textContent = before;

        // Create chip element
        const temp = document.createElement('div');
        temp.innerHTML = buildChipHtml(mention);
        const chipEl = temp.firstElementChild as HTMLElement;

        // Insert chip and trailing space
        const parent = targetNode.parentNode;
        if (parent) {
          const afterNode = document.createTextNode('\u00A0' + after);
          const nextSibling = targetNode.nextSibling;
          parent.insertBefore(chipEl, nextSibling);
          parent.insertBefore(afterNode, chipEl.nextSibling);

          // Update internal HTML ref
          htmlRef.current = el.innerHTML;

          // Place cursor after the chip
          const selection = window.getSelection();
          if (selection) {
            const range = document.createRange();
            range.setStart(afterNode, 1);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }

          // Notify parent of text change
          onTextChange(extractText(el));
        }
      }

      el.focus();
    },
  }));

  // Sync mentions into the HTML when mentions array changes externally (e.g. after selection from menu)
  useEffect(() => {
    const currentIds = mentions.map(m => m.id).join(',');
    if (currentIds === prevMentionIds.current) return;
    prevMentionIds.current = currentIds;

    const el = editableRef.current;
    if (!el) return;

    const currentText = extractText(el);
    const newHtml = buildHtml(currentText, mentions);
    if (newHtml !== htmlRef.current) {
      htmlRef.current = newHtml;
      el.innerHTML = newHtml;
      // Place cursor at end
      placeCursorAtEnd(el);
    }
  }, [mentions]);

  const handleChange = useCallback((evt: ContentEditableEvent) => {
    const el = editableRef.current;
    if (!el) return;

    htmlRef.current = evt.target.value;

    const text = extractText(el);
    onTextChange(text);

    // Check for @ trigger
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const container = range.startContainer;
      if (container.nodeType === Node.TEXT_NODE) {
        const textBefore = (container.textContent || '').slice(0, range.startOffset);
        if (textBefore.endsWith('@')) {
          onAtTrigger();
        } else if (!text.includes('@')) {
          onAtRemoved();
        }
      }
    }

    // Check for / trigger at position 0 (slash commands)
    if (onSlashTrigger && text.startsWith('/')) {
      const filter = text.slice(1).split(/\s/)[0] || '';
      onSlashTrigger(filter);
    } else if (onSlashRemoved) {
      onSlashRemoved();
    }

    // Check if any mentions were deleted from the DOM
    const domMentions = extractMentions(el);
    if (domMentions.length !== mentions.length) {
      onMentionsChange(domMentions);
    }
  }, [mentions, onTextChange, onMentionsChange, onAtTrigger, onAtRemoved]);

  // Use a ref for onSubmit to avoid stale closures
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  // Attach keydown directly to the DOM element to ensure it always fires
  useEffect(() => {
    const el = editableRef.current;
    if (!el) return;

    const handleKeyDown = (evt: KeyboardEvent) => {
      if (isComposing.current) return;

      if (evt.key === 'Enter' && !evt.shiftKey) {
        evt.preventDefault();
        if (!suppressSubmitRef.current) {
          onSubmitRef.current();
        }
      }
    };

    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleCompositionStart = useCallback(() => {
    isComposing.current = true;
  }, []);

  const handleCompositionEnd = useCallback(() => {
    isComposing.current = false;
  }, []);

  return (
    <ContentEditable
      innerRef={editableRef}
      html={htmlRef.current}
      onChange={handleChange}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      disabled={disabled}
      className={cn(
        'mention-input min-h-[40px] max-h-[200px] overflow-y-auto w-full bg-transparent px-3 py-2 text-[15px] md:text-base outline-none whitespace-pre-wrap break-words',
        'empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground empty:before:pointer-events-none',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
      data-placeholder={placeholder}
      role="textbox"
      aria-multiline="true"
    />
  );
});

MentionInput.displayName = 'MentionInput';

// Insert a mention chip at the current cursor position, replacing the trailing @
export function insertMentionAtCursor(
  el: HTMLElement,
  mention: Mention,
): void {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  const container = range.startContainer;

  // Find and remove the trailing @ in the text node
  if (container.nodeType === Node.TEXT_NODE) {
    const text = container.textContent || '';
    const offset = range.startOffset;
    const atIndex = text.lastIndexOf('@', offset - 1);

    if (atIndex !== -1) {
      // Remove the @ character
      const before = text.slice(0, atIndex);
      const after = text.slice(offset);
      container.textContent = before;

      // Create chip element
      const chip = document.createElement('span');
      chip.contentEditable = 'false';
      chip.dataset.mentionId = mention.id;
      chip.dataset.mentionType = mention.type;
      chip.dataset.mentionName = mention.name;
      chip.className = 'mention-chip';
      chip.innerHTML = buildChipHtml(mention);
      // The buildChipHtml wraps in a span, but we already have the outer span.
      // Use the inner content directly.
      const temp = document.createElement('div');
      temp.innerHTML = buildChipHtml(mention);
      const chipEl = temp.firstElementChild as HTMLElement;

      // Insert chip after the text node
      const parent = container.parentNode;
      if (parent) {
        const afterNode = document.createTextNode('\u00A0' + after);
        const nextSibling = container.nextSibling;
        parent.insertBefore(chipEl, nextSibling);
        parent.insertBefore(afterNode, chipEl.nextSibling);

        // Place cursor after the chip
        const newRange = document.createRange();
        newRange.setStart(afterNode, 1); // After the nbsp
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
    }
  }
}

function placeCursorAtEnd(el: HTMLElement) {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
}

export default MentionInput;
